import React, { useState, useRef, useCallback, useContext } from 'react';
import { useDispatch } from 'react-redux';
import { ADD_DISCIPLINE } from '../../Reducers/Actions';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@emotion/react';
import { SnackBarContext } from '../../Context/SnackBarContextProvider';
import { tokens } from '../../theme';
import FormInput from '../../SystemDesign/FormInput';
import ColorSelector from '../../SystemDesign/ColorSelector';
import IconSelector from '../../SystemDesign/IconSelector';
import { useCreateDisciplineMutation } from '../../services/SkillsManagement';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import MuiDialog from '../../SystemDesign/MuiDialog';
import AddIcon from '@mui/icons-material/Add';
import { PrimaryButton } from '../../SystemDesign/Styles';

// Import Lucide discipline icons
import {
  Shield,
  Wrench,
  Anchor,
  Zap,
  BookOpen,
  Code
} from "lucide-react";

const COLOR_OPTIONS = [
  { name: 'red', value: '#F44336' },
  { name: 'orange', value: '#FF9800' },
  { name: 'yellow', value: '#FFC107' },
  { name: 'green', value: '#4CAF50' },
  { name: 'blue', value: '#2196F3' },
  { name: 'purple', value: '#9C27B0' },
  { name: 'pink', value: '#E91E63' },
  { name: 'cyan', value: '#00BCD4' },
];

const ICON_OPTIONS = [
  { name: 'QHSE', icon: Shield, displayName: 'QHSE', lucideName: 'Shield' },
  { name: 'Drilling', icon: Wrench, displayName: 'Drilling', lucideName: 'Wrench' },
  { name: 'Marine', icon: Anchor, displayName: 'Marine', lucideName: 'Anchor' },
  { name: 'Electrical', icon: Zap, displayName: 'Electrical', lucideName: 'Zap' },
  { name: 'Mechanic', icon: Wrench, displayName: 'Mechanic', lucideName: 'Wrench' },
  { name: 'Core', icon: BookOpen, displayName: 'Core', lucideName: 'BookOpen' },
];

const AddDiscipline = ({ open, onClose, onSubmit }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  
  // Access the SnackBar context for showing success/error messages
  const { setOpenSnackBar, setInfoAlert } = useContext(SnackBarContext);
  
  // State to track form validity and dirty state
  const [isFormValid, setIsFormValid] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);
  
  // Create a ref to access Formik methods from outside the Formik component
  const formRef = useRef(null);
  
  // Make sure we're using the correct default color that matches our parseDepartment function
  const [selectedColor, setSelectedColor] = useState('#2196F3'); // Default blue
  
  // State for selected icon
  const [selectedIcon, setSelectedIcon] = useState('QHSE'); // Default icon name
  
  // RTK Query mutation hook
  const [createDiscipline, { isLoading }] = useCreateDisciplineMutation();
  
  // Validation schema
  const validationSchema = Yup.object({
    name: Yup.string().required('Department name is required')
  });
  
  // Function to trigger form submission via MuiDialog action button
  const submitForm = useCallback(() => {
    if (formRef.current) {
      formRef.current.handleSubmit();
    }
  }, []);

  const handleSubmit = async (values, { resetForm, setSubmitting }) => {
    try {
      // Start submission
      setSubmitting(true);
      console.log('Creating department with selected color:', selectedColor);
      
      // Find the selected icon object
      const selectedIconObj = ICON_OPTIONS.find(option => option.name === selectedIcon);
      
      // Create the structured icon object similar to the backend format
      const iconData = {
        type: 'Lucide',
        name: selectedIconObj ? selectedIconObj.lucideName : 'HealthAndSafety',
        props: {
          size: 24,
          strokeWidth: 2
        }
      };
      
      // Call the API to create the discipline with name, color, and structured icon
      const result = await createDiscipline({
        body: {
          name: values.name.trim(),
          color: selectedColor,
          icon: JSON.stringify(iconData)
        }
      }).unwrap();
      
      
      // The discipline data is nested inside the 'data' property of the response
      const disciplineData = result.data || {};
      const disciplineId = disciplineData.discipline_id;
      
     
      if (!disciplineId) {
        console.error('No discipline_id in API response data:', result);
        // Show error in SnackBar if no discipline_id was returned
        setInfoAlert({
          severity: 'error',
          message: 'Could not create department: No department ID returned from server'
        });
        setOpenSnackBar(true);
        setSubmitting(false);
        return;
      }
      
      // Prepare complete discipline object for Redux store
      const disciplineForStore = {
        discipline_id: disciplineId,
        name: disciplineData.name || values.name,
        color: selectedColor,
        createdAt: disciplineData.createdAt || new Date().toISOString(),
        updatedAt: disciplineData.updatedAt || new Date().toISOString(),
        unitCount: 0,
      };
      
      dispatch({
        type: ADD_DISCIPLINE,
        payload: disciplineForStore
      });
      
      // Notify parent component
      if (onSubmit) {
        onSubmit(disciplineForStore);
      } else {
        console.log('No onSubmit callback provided');
      }
      
      // Store the color in localStorage for future use
      try {
        const disciplineColors = JSON.parse(localStorage.getItem('disciplineColors') || '{}');
        disciplineColors[disciplineId] = selectedColor;
        localStorage.setItem('disciplineColors', JSON.stringify(disciplineColors));
        console.log('Updated localStorage with color:', selectedColor);
      } catch (e) {
        console.error('Error updating localStorage:', e);
      }
      
      // Show success message
      setInfoAlert({
        severity: 'success',
        message: `Department "${values.name}" successfully created`,
      });
      setOpenSnackBar(true);
      
      // Reset form and close dialog
      resetForm();
      onClose();
      
      // This matches AddClient behavior - call onSubmit last
      if (onSubmit) {
         onSubmit(disciplineForStore);
      }
      
      // We no longer need to force reload since we've properly fixed the state management
      
    } catch (error) {
      console.error('Error creating discipline:', error);
      // Show error message from API or a default message
      let errorMessage = 'Failed to create department';
      if (error?.data) {
        console.log('Error data:', error.data);
        errorMessage += ': ' + JSON.stringify(error.data);
      }
      
      setInfoAlert({
        severity: 'error',
        message: errorMessage,
      });
      setOpenSnackBar(true);
    } finally {
      setSubmitting(false);
    }
  };

  // Create dialog content for MuiDialog (without the submit button)
  const dialogContent = () => (
    <Formik
      initialValues={{ name: '' }}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      innerRef={formRef}
      validateOnMount={true}
    >
      {(formikProps) => {
        // Update parent about form state changes without using hooks
        const { isValid, dirty } = formikProps;
        if (formRef.current && (formRef.current.isValid !== isValid || formRef.current.dirty !== dirty)) {
          setTimeout(() => {
            setIsFormValid(isValid);
            setIsFormDirty(dirty);
          }, 0);
        }
        
        return (
          <Form id="add-discipline-form">
            {/* Form fields */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="body1" fontWeight="500" mb={1}>
                Department Name
              </Typography>
              
              <FormInput
                name="name"
                placeholder="Ex: QHSE, Drilling..."
                variant="outlined"
              />
            </Box>
            
            {/* Color selector */}
            <ColorSelector 
              colors={COLOR_OPTIONS}
              selectedColor={selectedColor}
              onColorChange={setSelectedColor}
            />
            
            {/* Icon selector */}
            <IconSelector 
              icons={ICON_OPTIONS}
              selectedIcon={selectedIcon}
              onIconChange={setSelectedIcon}
            />
          </Form>
        );
      }}
    </Formik>
  );

  return (
    <MuiDialog 
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      icon={<AddIcon />}
      title="Create New Department"
      subtitle="Add a new department to your structure"
      content={dialogContent}
      existAction={true}
      actionTitle="Create Department"
      actionFunction={submitForm}
      isLoading={isLoading}
      actionType="primary"
      formId="add-discipline-form"
      disableAction={!(isFormValid && isFormDirty)}
    />
  );
};

export default AddDiscipline;